/**
 * CoSingery — 播放器**純邏輯**（唔碰 DOM／React → 可以機械測試）
 *
 * 為何獨立：播放器最易出錯嘅**唔係** UI，而係「邊首歌、第幾個版本、flac 得唔得、seek 去邊」呢啲決定 ——
 *   抽成純函數就驗得到（`scripts/player-smoke.mjs`，入 `npm run build`）。
 */

/** 由 API 嚟嘅歌（只需要播放相關欄位） */
export interface SongLike {
  id: string;
  title: string;
  status: string;
  audioKey: string | null;
  lyrics?: string | null;
  /** provider／系統錯誤原文（有就照實顯示） */
  error?: string | null;
  variants?: { index: number; durationMs: number | null; hasHifi: boolean }[];
  pickedIndex?: number;
}

export interface Track {
  id: string;
  title: string;
  /** 一般音質（mp3）—— 用**用戶揀住**嗰個版本（後端 `audio_key` 已經係佢） */
  audioSrc: string;
  /** 高音質（flac）—— 只有揀住嗰個版本抓咗 flac 才有 */
  hifiSrc: string | null;
  durationMs: number | null;
  /** 歌詞（播放器頁顯示用；`null` ＝ 冇） */
  lyrics: string | null;
}

/**
 * 擁有者引用（**弱身份** —— 唔係登入；`P-45`）
 *   · `sessionId` ＝ 一次對話（舊行為）
 *   · `deviceId`  ＝ 一部機（**個人 library／播放清單**；`decisions/018`）
 * ⚠️ 後端接受**任何一個**對得上（見 `functions/_lib/song/routes.ts`）
 */
export interface OwnerRef {
  sessionId?: string | null;
  deviceId?: string | null;
}

const audioUrl = (id: string, owner: OwnerRef | string): string => {
  /** 向後兼容：直接傳 string ＝ `sessionId`（`player-smoke` 舊斷言靠呢個） */
  const ref: OwnerRef = typeof owner === 'string' ? { sessionId: owner } : owner;
  const q = new URLSearchParams();
  if (ref.sessionId) q.set('sessionId', ref.sessionId);
  if (ref.deviceId) q.set('deviceId', ref.deviceId);
  return `/api/song/audio/${id}?${q.toString()}`;
};

/**
 * 音頻網址（**對外**；可以加版本／高音質）—— 單一定義點。
 *
 * ⭐ 2026-09-22（`decisions/026` §分享）：分享 mp3 都要用同一條規則砌 → 唔准喺 `ChatPage` 另砌一份
 *   （咁樣「擁有者檢查」嘅 query 就唔會兩處走樣）。
 * ⚠️ `hifi` **唔准**由呼叫者亂傳：只有 `hasHifi === true` 先好傳（後端冇檔案會 404，見 `trackFromSong`）。
 */
export function songAudioUrl(
  id: string,
  owner: OwnerRef | string,
  opts: { variant?: number; hifi?: boolean } = {},
): string {
  const base = audioUrl(id, owner);
  const parts: string[] = [];
  if (opts.variant !== undefined) parts.push(`variant=${opts.variant}`);
  if (opts.hifi) parts.push('hifi=1');
  return parts.length > 0 ? `${base}&${parts.join('&')}` : base;
}

/**
 * ⭐ **首歌而家有冇音頻可以播**（2026-09-23 修；產品負責人：「訊息內 ▶ 第一次撳冇反應」）——
 *   根因：`trackFromSong()` 要有 `audioKey` 才砌得出 track；冇 → 之前呼叫者**靜靜 return**
 *   （用戶以為壞咗）。呢個 predicate 係**單一判斷點**：UI 要用佢決定「可唔可以播／要唔要照實講」。
 */
export function canPlaySong(song: Pick<SongLike, 'audioKey'> | null | undefined): boolean {
  return Boolean(song?.audioKey);
}

/**
 * 歌 → 播放清單項目。**冇音頻就回 `null`**（唔准扮有歌可以播 —— `rules.md` B2）。
 */
export function trackFromSong(song: SongLike, owner: OwnerRef | string): Track | null {
  if (!song.audioKey) return null;
  const pickedIndex = song.pickedIndex ?? 0;
  const picked = song.variants?.find((v) => v.index === pickedIndex) ?? null;
  return {
    id: song.id,
    title: song.title,
    audioSrc: audioUrl(song.id, owner),
    /** ⚠️ 唔可以亂做 flac URL：只有 `hasHifi === true`（後端真係有檔案）才提供 */
    hifiSrc: picked?.hasHifi ? songAudioUrl(song.id, owner, { variant: pickedIndex, hifi: true }) : null,
    durationMs: picked?.durationMs ?? null,
    lyrics: song.lyrics ?? null,
  };
}

/** 由歌單砌播放清單（跳過未有音頻嘅） */
export function tracksFromSongs(songs: SongLike[], owner: OwnerRef | string): Track[] {
  return songs.map((s) => trackFromSong(s, owner)).filter((t): t is Track => t !== null);
}

/**
 * ⭐ **播放模式**（產品負責人 2026-09-24：「the player should include the basic function of a player,
 *   like add to my favourite, **repeat one, repeat all, random play** etc」）
 *   · `off` ＝ 播完就停（順播到尾）
 *   · `one` ＝ **單曲循環**（同一首再播）
 *   · `all` ＝ **全部循環**（尾接返頭）
 */
export type RepeatMode = 'off' | 'one' | 'all';

export interface PickOpts {
  repeat: RepeatMode;
  shuffle: boolean;
  /** 0–1（測試注入用；唔傳 ＝ `Math.random()`） */
  random?: number;
}

const rand = (r?: number): number => Math.max(0, Math.min(0.999999, r ?? Math.random()));

/**
 * **下一個 index**（自動接落去／手動〔下一首〕共用）—— `-1` ＝ 冇下一首（呼叫者要**停**）
 *   · 隨機（`shuffle`）→ 由其他 `length-1` 首隨機揀（**唔會即刻重複同一首**）
 *   · 正常 → 下一格；到尾 → `all` 就返 0，否則 -1
 *   ⚠️ `repeat: 'one'` **唔喺呢度處理**（單曲循環係「重播同一首」；手動〔下一首〕照樣去下一首）
 */
export function pickNextIndex(length: number, current: number, opts: PickOpts): number {
  if (length <= 0) return -1;
  if (current < 0) return 0;
  if (length === 1) return opts.repeat === 'all' ? 0 : -1;
  if (opts.shuffle) {
    const idx = Math.floor(rand(opts.random) * (length - 1));
    return idx >= current ? idx + 1 : idx;
  }
  const next = current + 1;
  if (next < length) return next;
  return opts.repeat === 'all' ? 0 : -1;
}

/** **上一個 index**（手動／耳機掣）：隨機 → 隨機揀另一首；否則退一格（到頭就停喺第一首） */
export function pickPrevIndex(length: number, current: number, opts: { shuffle: boolean; random?: number }): number {
  if (length <= 0) return -1;
  if (opts.shuffle && length > 1) {
    const idx = Math.floor(rand(opts.random) * (length - 1));
    return idx >= current ? idx + 1 : idx;
  }
  const prev = current - 1;
  return prev >= 0 ? prev : 0;
}

/** 毫秒 → `m:ss`（`-` 表示未知） */
export function formatTime(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return '-';
  const total = Math.floor(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** seek 位置要夾喺 `[0, duration]`（未知長度 → 唔准亂跳，回 0） */
export function clampSeek(ms: number, durationMs: number | null): number {
  if (durationMs === null || !Number.isFinite(durationMs) || durationMs <= 0) return 0;
  return Math.min(Math.max(ms, 0), durationMs);
}

/** 播放進度 0–1（未知長度回 0，唔會出現 NaN 令 UI 爆） */
export function progressRatio(positionMs: number, durationMs: number | null): number {
  if (durationMs === null || durationMs <= 0) return 0;
  return Math.min(1, Math.max(0, positionMs / durationMs));
}
